import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Heading from "../../components/ui/Heading";
import PhoneLink from "../../components/ui/PhoneLink";
import { useApi } from "../../context/ApiProvider";
import ImageSkeleton from "../../components/skeletons/ImageSkeleton";
import TextSkeleton from "../../components/skeletons/TextSkeleton";

const LeadershipProfile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const api = useApi();
  const searchParams = new URLSearchParams(location.search);
  const slug = searchParams.get("slug");

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return undefined;
    }
    let isMounted = true;
    setLoading(true);
    api
      .getLeadershipProfile(slug)
      .then((result) => {
        if (isMounted) setProfile(result || null);
      })
      .catch(() => {})
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [slug, api]);

  return (
    <>
      <Header />

      <div className="mx-auto mb-20 mt-16 max-w-4xl px-4">
        {loading ? (
          <div className="rounded-sm bg-white p-6 shadow-sm sm:p-10">
            <div className="mb-8 flex flex-col items-center gap-6 sm:flex-row sm:items-start">
              <ImageSkeleton className="h-36 w-36 flex-shrink-0 rounded-full" />
              <div className="w-full">
                <TextSkeleton lines={2} />
              </div>
            </div>
            <TextSkeleton lines={6} />
          </div>
        ) : !profile ? (
          <div className="rounded-sm border border-dashed border-stone-300 bg-white p-10 text-center">
            <Heading level={4} className="mb-2 text-primary-800">
              Profile not found
            </Heading>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-sm font-semibold text-primary-700 underline underline-offset-2"
            >
              ← Back
            </button>
          </div>
        ) : (
          <div className="rounded-sm bg-white p-6 shadow-sm ring-1 ring-stone-200 sm:p-10">
            <div className="mb-8 flex flex-col items-center gap-6 border-b border-stone-100 pb-8 text-center sm:flex-row sm:items-start sm:text-left">
              <img
                src={profile.PHOTO}
                alt={profile.NAME}
                loading="lazy"
                className="h-36 w-36 flex-shrink-0 rounded-full object-cover ring-4 ring-stone-100"
              />
              <div>
                <Heading level={3} className="mb-1 text-primary-900">
                  {profile.NAME}
                </Heading>
                <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-accent-600">
                  {profile.TITLE}
                </p>
                {profile.PHONE && <PhoneLink number={profile.PHONE} className="text-sm text-secondary-700" />}
              </div>
            </div>

            {profile.BIO && (
              <div className="mb-8">
                <Heading level={5} className="mb-3 text-primary-800">
                  Biography
                </Heading>
                <p className="leading-relaxed text-stone-700">{profile.BIO}</p>
              </div>
            )}

            {profile.MESSAGE && (
              <div>
                <Heading level={5} className="mb-3 text-primary-800">
                  Message
                </Heading>
                {profile.MESSAGE.split("\n\n").map((para, i) => (
                  <p key={i} className="mb-4 leading-relaxed text-stone-700 last:mb-0">
                    {para}
                  </p>
                ))}
              </div>
            )}

            {!profile.BIO && !profile.MESSAGE && (
              <p className="text-sm text-stone-500">
                A full biography for {profile.NAME} hasn't been published yet.
              </p>
            )}
          </div>
        )}
      </div>

      <Footer />
    </>
  );
};

export default LeadershipProfile;
